const cartDrawerChatStyles = new WeakMap();

const getCartDrawerChatTargets = () => {
  const candidates = Array.from(document.body.querySelectorAll('iframe, div, shopify-chat, shopify-inbox'));
  return candidates.filter((element) => {
    const id = element.id?.toLowerCase() || '';
    const className = typeof element.className === 'string' ? element.className.toLowerCase() : '';
    const title = element.getAttribute('title')?.toLowerCase() || '';
    const src = element.getAttribute('src')?.toLowerCase() || '';

    return (
      id.includes('shopifychat') ||
      id.includes('shopify-chat') ||
      id.includes('inbox') ||
      className.includes('shopify-chat') ||
      className.includes('shopify-inbox') ||
      title.includes('chat') ||
      src.includes('inbox')
    );
  });
};

const rememberCartDrawerChatStyle = (element) => {
  if (cartDrawerChatStyles.has(element)) return;

  cartDrawerChatStyles.set(element, {
    left: element.style.left,
    right: element.style.right,
    zIndex: element.style.zIndex,
    transform: element.style.transform,
  });
};

const restoreCartDrawerChatPosition = () => {
  getCartDrawerChatTargets().forEach((element) => {
    const original = cartDrawerChatStyles.get(element);

    if (original) {
      element.style.left = original.left;
      element.style.right = original.right;
      element.style.zIndex = original.zIndex;
      element.style.transform = original.transform;
      cartDrawerChatStyles.delete(element);
    } else {
      element.style.removeProperty('left');
      element.style.removeProperty('right');
      element.style.removeProperty('z-index');
      element.style.removeProperty('transform');
    }
  });
};

const syncCartDrawerChatPosition = () => {
  const drawer = document.querySelector('cart-drawer.active');
  const drawerInner = drawer?.querySelector('.drawer__inner');

  if (!drawer || !drawerInner) {
    restoreCartDrawerChatPosition();
    return;
  }

  const drawerWidth = Math.ceil(drawerInner.getBoundingClientRect().width);
  const leftSpace = window.innerWidth - drawerWidth;
  const placeBesideDrawer = leftSpace >= 96;

  document.documentElement.style.setProperty('--lm-cart-drawer-width', `${drawerWidth}px`);
  document.body.classList.add('lm-cart-drawer-active');

  getCartDrawerChatTargets().forEach((element) => {
    rememberCartDrawerChatStyle(element);
    element.style.setProperty('z-index', '1001', 'important');
    element.style.setProperty('transform', 'none', 'important');

    if (placeBesideDrawer) {
      element.style.removeProperty('left');
      element.style.setProperty('right', `calc(${drawerWidth}px + 1.6rem)`, 'important');
    } else {
      element.style.removeProperty('right');
      element.style.setProperty('left', '0.8rem', 'important');
    }
  });
};

const observeCartDrawerChatTargets = () => {
  if (window.lmCartDrawerChatObserver) return;

  window.lmCartDrawerChatObserver = new MutationObserver(() => {
    if (document.querySelector('cart-drawer.active')) {
      window.setTimeout(syncCartDrawerChatPosition, 50);
    }
  });

  window.lmCartDrawerChatObserver.observe(document.body, { childList: true, subtree: true });
  window.addEventListener('resize', () => {
    if (document.querySelector('cart-drawer.active')) syncCartDrawerChatPosition();
  });
};

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    observeCartDrawerChatTargets();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      syncCartDrawerChatPosition();
      window.setTimeout(syncCartDrawerChatPosition, 250);
      window.setTimeout(syncCartDrawerChatPosition, 800);
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    document.body.classList.remove('lm-cart-drawer-active');
    document.documentElement.style.removeProperty('--lm-cart-drawer-width');
    restoreCartDrawerChatPosition();
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
